import { DialogWrapper } from "Dialogs/wrapper";
import { declOfNum } from "Utils/string/decl-of-num";

export class ViewModel extends DialogWrapper {
  constructor(params, element) {
    super(params, element);

    this.clickCoordinates = params.clickCoordinates || [];
    this.screenPath = params.screenPath || [];
    this.testCompletion = params.testCompletion || [];
    this.testScreen = params.testScreen || [];
    this.startScreenNodeId =
      params.startScreenNodeId
      ?? params.question?.scenarioTest?.figma_start_screen_node_id
      ?? params.question?.scenarioTest?.start_screen_node_id
      ?? null;
    this.question = params.question;
    this.isSuccess = params.question && typeof params.question.isSuccess === 'function'
      ? params.question.isSuccess()
      : false;

    this._screenMap = {};
    (this.testScreen || []).forEach((screen) => {
      if (screen && screen.node_id) {
        this._screenMap[screen.node_id] = screen;
      }
    });

    this._clicksByNodeId = {};
    (this.clickCoordinates || []).forEach((coord) => {
      if (!coord) return;
      const nodeId = coord.node_id || coord.nodeId || coord.screen_node_id;
      if (!nodeId) return;
      if (!this._clicksByNodeId[nodeId]) {
        this._clicksByNodeId[nodeId] = [];
      }
      this._clicksByNodeId[nodeId].push(coord);
    });

    this._completionNodeIds = new Set();
    (this.testCompletion || []).forEach((completion) => {
      if (!completion) return;
      if (completion.type === 'final_screen' && completion.screen_node_id) {
        this._completionNodeIds.add(completion.screen_node_id);
      } else if (completion.type === 'scenario_path' && Array.isArray(completion.path) && completion.path.length > 0) {
        if (this.isSuccess) {
          completion.path.forEach((nodeId) => {
            if (this.screenPath.includes(nodeId)) {
              this._completionNodeIds.add(nodeId);
            }
          });
        } else {
          completion.path.forEach((nodeId) => this._completionNodeIds.add(nodeId));
        }
      }
    });

    this.screens = this._buildScreenList();

    // При переходе по ссылке «для экрана {Название}» из статуса ответа
    // сайдшит открывается с выбранным последним экраном пути.
    this.selectedIndex = ko.observable(
      params.selectLastScreen ? Math.max(0, this.screens.length - 1) : 0
    );

    this.isMobile = ko.observable(window.innerWidth <= 768);
    this._resizeHandler = () => this.isMobile(window.innerWidth <= 768);
    window.addEventListener('resize', this._resizeHandler);

    this.selectedScreen = ko.computed(() => {
      return this.screens[this.selectedIndex()] || null;
    });

    this.selectedClickCount = ko.computed(() => {
      const screen = this.selectedScreen();
      if (!screen) return 0;
      return (this._clicksByNodeId[screen.node_id] || []).length;
    });

    this.selectedClickCoordinates = ko.computed(() => {
      const screen = this.selectedScreen();
      if (!screen) return [];
      return this._clicksByNodeId[screen.node_id] || [];
    });

    this._viewerImageNaturalWidth = ko.observable(0);
    this._viewerImageNaturalHeight = ko.observable(0);

    this.selectedScalePercent = ko.computed(() => {
      const screen = this.selectedScreen();
      const nw = this._viewerImageNaturalWidth();
      const nh = this._viewerImageNaturalHeight();
      if (!screen || !screen.width || !screen.height || !nw || !nh) return null;
      const scaleX = nw / screen.width;
      const scaleY = nh / screen.height;
      const scale = Math.round(Math.min(scaleX, scaleY) * 100);
      return scale + '%';
    });

    this.selectScreen = this.selectScreen.bind(this);
    this.getClickDotStyle = this.getClickDotStyle.bind(this);
    this.getClickTooltip = this.getClickTooltip.bind(this);
    this.showArrowAfter = this.showArrowAfter.bind(this);
    this.onViewerImageLoad = this.onViewerImageLoad.bind(this);
  }

  selectScreen(index) {
    this.selectedIndex(index);
  }

  _resolveScreenName(nodeId, screenData) {
    // Подпись стартового экрана всегда фиксированная — «Стартовый».
    if (
      this.startScreenNodeId
      && nodeId
      && String(this.startScreenNodeId) === String(nodeId)
    ) {
      return 'Стартовый';
    }

    return screenData && screenData.name ? String(screenData.name).trim() : '';
  }

  _buildScreenList() {
    const screenPath = this.screenPath || [];
    if (!screenPath.length) return [];

    return screenPath.map((nodeId) => {
      const screenData = this._screenMap[nodeId] || null;
      const isCompletion = this._completionNodeIds.has(nodeId);
      const areas = this._normalizeAreas(screenData ? screenData.areas : null);
      return {
        node_id: nodeId,
        name: this._resolveScreenName(nodeId, screenData),
        thumbnail_url: screenData ? (screenData.thumbnail_url || '') : '',
        width: screenData ? screenData.width : null,
        height: screenData ? screenData.height : null,
        isCompletion,
        clicks: this._clicksByNodeId[nodeId] || [],
        areas,
        hasAreas: areas.length > 0,
        areasTooltip: `Для экрана настроено ${areas.length} ${declOfNum(areas.length, ['область', 'области', 'областей'])}`,
      };
    });
  }

  _normalizeAreas(areas) {
    if (!Array.isArray(areas)) return [];

    return areas
      .filter((area) => area && typeof area === 'object')
      .map((area, index) => {
        const name = (area.name || '').trim();
        return {
          id: area.id !== null && area.id !== undefined ? Number(area.id) : null,
          name: this._escapeHtml(name) || `Область ${index + 1}`,
        };
      });
  }

  // Тултип-биндинг вставляет текст через innerHTML — экранируем название области.
  _escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  getClickTooltip(coord) {
    if (!coord) return '';

    const screen = this.selectedScreen();
    const areas = (screen && screen.areas) || [];
    const rawAreaIds = Array.isArray(coord.area_ids) ? coord.area_ids : [];
    const areaIds = rawAreaIds
      .filter((v) => v !== null && v !== undefined)
      .map((v) => Number(v));

    let label;
    if (areaIds.length === 0) {
      label = 'Клик вне области';
    } else {
      const names = areaIds
        .map((id) => {
          const area = areas.find((item) => item.id === id);
          return area ? area.name : null;
        })
        .filter((name) => name);
      label = names.length ? names.join(', ') : 'Клик вне области';
    }

    const seconds = coord.screen_click_time;
    if (seconds === null || seconds === undefined) return label;
    return `${label} / ${seconds}с`;
  }

  getClickDotStyle(coord) {
    if (!coord) return {};

    const x = parseFloat(coord.x !== undefined ? coord.x : (coord.click_x || 0));
    const y = parseFloat(coord.y !== undefined ? coord.y : (coord.click_y || 0));

    const screen = this.selectedScreen();
    const coordSpaceW = screen && screen.width ? screen.width : 0;
    const coordSpaceH = screen && screen.height ? screen.height : 0;

    if (coordSpaceW <= 0 || coordSpaceH <= 0) return {};

    const propX = x > 1 ? x / coordSpaceW : x;
    const propY = y > 1 ? y / coordSpaceH : y;

    return {
      position: 'absolute',
      left: (propX * 100).toFixed(4) + '%',
      top: (propY * 100).toFixed(4) + '%',
      transform: 'translate(-50%, -50%)',
    };
  }

  showArrowAfter(index) {
    return this.screens.length > 1 && index < this.screens.length - 1;
  }

  onViewerImageLoad(imgEl) {
    if (!imgEl) return;
    this._updateViewerSize(imgEl);

    if (this._viewerImageResizeObserver) {
      this._viewerImageResizeObserver.disconnect();
    }
    this._viewerImageResizeObserver = new ResizeObserver(() => {
      this._updateViewerSize(imgEl);
    });
    this._viewerImageResizeObserver.observe(imgEl);
  }

  _updateViewerSize(imgEl) {
    const rect = imgEl.getBoundingClientRect();
    this._viewerImageNaturalWidth(rect.width);
    this._viewerImageNaturalHeight(rect.height);
  }

  dispose() {
    window.removeEventListener('resize', this._resizeHandler);
    if (this._viewerImageResizeObserver) {
      this._viewerImageResizeObserver.disconnect();
    }
    if (super.dispose) super.dispose();
  }
}
